<template>
  <div class="py-4 container-fluid">
    <!-- Header -->
    <div class="d-flex align-items-center mb-4">
      <button class="btn btn-outline-secondary btn-sm me-3" @click="$router.push('/warehouses')">
        <i class="fas fa-arrow-left me-1"></i> 返回
      </button>
      <div class="flex-grow-1">
        <h5 class="mb-0">{{ warehouse.name || '倉庫詳情' }}</h5>
        <p class="text-xs text-muted mb-0" v-if="warehouse.code">
          {{ warehouse.code }} ·
          {{ warehouse.type === 'online' ? '線上商城' : '實體門市' }}
          <span v-if="warehouse.city"> · {{ warehouse.city }}</span>
        </p>
      </div>
      <span
        class="badge ms-2"
        :class="warehouse.is_active ? 'bg-success' : 'bg-secondary'"
      >{{ warehouse.is_active ? '啟用中' : '已停用' }}</span>
    </div>

    <!-- Stats Summary -->
    <div class="row mb-4" v-if="items.length > 0">
      <div class="col-md-3 col-6 mb-3">
        <div class="card text-center py-3">
          <div class="h4 mb-0">{{ items.length }}</div>
          <div class="text-xs text-muted">分配商品數</div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="card text-center py-3">
          <div class="h4 mb-0 text-primary">{{ totalAllocated }}</div>
          <div class="text-xs text-muted">寄賣數量合計</div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="card text-center py-3">
          <div class="h4 mb-0 text-danger">{{ totalSold }}</div>
          <div class="text-xs text-muted">已賣出合計</div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="card text-center py-3">
          <div class="h4 mb-0 text-success">{{ totalRemaining }}</div>
          <div class="text-xs text-muted">剩餘在店合計</div>
        </div>
      </div>
    </div>

    <!-- Inventory Table -->
    <div class="card">
      <div class="card-header pb-0 d-flex justify-content-between align-items-center">
        <h6 class="mb-0">商品庫存分配</h6>
        <button class="btn btn-primary btn-sm" @click="openAddModal">
          <i class="fas fa-plus me-1"></i> 新增商品分配
        </button>
      </div>
      <div class="card-body p-0">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status"></div>
        </div>
        <div v-else-if="items.length === 0" class="text-center py-5 text-muted">
          <i class="fas fa-box-open fa-2x mb-2 d-block opacity-4"></i>
          此倉庫尚無商品分配
        </div>
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="bg-light">
              <tr>
                <th class="ps-4 text-xs text-uppercase text-muted fw-bold py-3">商品名稱</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3">SKU / 規格</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-center">總庫存</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-center">寄賣數量</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-center">已賣出</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-center">剩餘在店</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3">備註</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-end pe-4">操作</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in items" :key="item.id">
              <tr>
                <!-- Product name -->
                <td class="ps-4">
                  <div class="text-sm fw-bold">{{ item.product_name }}</div>
                  <div class="text-xs text-muted font-monospace">{{ item.product_code }}</div>
                </td>
                <!-- SKU / Variant -->
                <td class="text-sm">
                  <span v-if="item.variant_code" class="font-monospace text-xs">
                    {{ item.variant_code }}
                    <span v-if="item.variant_name" class="text-muted"> · {{ item.variant_name }}</span>
                  </span>
                  <span v-else class="text-muted text-xs">主商品</span>
                </td>
                <!-- 總庫存 -->
                <td class="text-center text-sm">{{ item.total_stock }}</td>
                <!-- 寄賣數量 (allocated_stock) -->
                <td class="text-center">
                  <input
                    v-if="editingItem === item.id"
                    v-model.number="editForm.allocated_stock"
                    type="number"
                    min="0"
                    class="form-control form-control-sm text-center"
                    style="width: 70px"
                    @keyup.esc="cancelEdit"
                  />
                  <span v-else class="fw-bold text-sm">{{ item.allocated_stock }}</span>
                </td>
                <!-- 已賣出 (sold_qty) -->
                <td class="text-center">
                  <input
                    v-if="editingItem === item.id"
                    v-model.number="editForm.sold_qty"
                    type="number"
                    min="0"
                    class="form-control form-control-sm text-center"
                    style="width: 70px"
                    @keyup.enter="saveEdit(item)"
                    @keyup.esc="cancelEdit"
                  />
                  <span v-else class="text-danger fw-semibold text-sm">{{ item.sold_qty ?? 0 }}</span>
                </td>
                <!-- 剩餘在店 (computed) -->
                <td class="text-center">
                  <span v-if="editingItem === item.id"
                    class="badge"
                    :class="(editForm.allocated_stock - editForm.sold_qty) >= 0 ? 'bg-success' : 'bg-danger'">
                    {{ Math.max(0, editForm.allocated_stock - editForm.sold_qty) }}
                  </span>
                  <span v-else class="badge"
                    :class="(item.remaining_in_store ?? item.allocated_stock) > 0 ? 'bg-success' : 'bg-secondary'">
                    {{ item.remaining_in_store ?? item.allocated_stock }}
                  </span>
                </td>
                <!-- 備註 -->
                <td class="text-sm text-muted">
                  <span v-if="editingItem === item.id">
                    <input
                      v-model="editForm.notes"
                      class="form-control form-control-sm"
                      placeholder="備註（選填）"
                      style="min-width: 120px"
                    />
                  </span>
                  <span v-else>{{ item.notes || '—' }}</span>
                </td>
                <!-- Actions -->
                <td class="text-end pe-4">
                  <template v-if="editingItem === item.id">
                    <button class="btn btn-sm btn-success me-1" @click="saveEdit(item)" :disabled="saving">
                      <i class="fas fa-check"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" @click="cancelEdit">
                      <i class="fas fa-times"></i>
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="btn btn-sm btn-outline-secondary me-1"
                      @click="startEdit(item)"
                      title="編輯"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      class="btn btn-sm me-1"
                      :class="expandedLogId === item.id ? 'btn-info' : 'btn-outline-info'"
                      @click="toggleLog(item)"
                      title="更新紀錄"
                    >
                      <i class="fas fa-history"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click="removeItem(item)"
                      title="移除"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </template>
                </td>
              </tr>
              <!-- Inline log panel (expands below the row) -->
              <tr v-if="expandedLogId === item.id">
                <td colspan="8" class="p-0">
                  <div class="bg-light border-top px-4 py-3">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span class="text-xs fw-bold text-muted text-uppercase tracking-wide">
                        <i class="fas fa-history me-1"></i> 更新紀錄
                      </span>
                      <span v-if="logLoading" class="spinner-border spinner-border-sm text-secondary"></span>
                    </div>
                    <div v-if="!logLoading && itemLogs.length === 0" class="text-muted text-xs py-1">
                      尚無紀錄
                    </div>
                    <table v-else class="table table-sm table-borderless mb-0" style="font-size:0.78rem">
                      <thead>
                        <tr class="text-muted">
                          <th style="width:140px">時間</th>
                          <th style="width:80px">操作</th>
                          <th>變更內容</th>
                          <th style="width:100px">操作人</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="log in itemLogs" :key="log.id">
                          <td class="text-muted">{{ log.created_at }}</td>
                          <td>
                            <span class="badge"
                              :class="log.action === 'created' ? 'bg-success' : log.action === 'deleted' ? 'bg-danger' : 'bg-warning text-dark'">
                              {{ log.action === 'created' ? '新增' : log.action === 'deleted' ? '刪除' : '更新' }}
                            </span>
                          </td>
                          <td>
                            <template v-if="log.action === 'updated' && log.changes">
                              <span v-for="(diff, field) in log.changes" :key="field" class="me-3">
                                <span class="text-muted">{{ fieldLabel(field) }}</span>：
                                <span class="text-danger">{{ diff.old }}</span>
                                <i class="fas fa-arrow-right mx-1 text-muted" style="font-size:0.65rem"></i>
                                <span class="text-success fw-bold">{{ diff.new }}</span>
                              </span>
                            </template>
                            <span v-else class="text-muted">{{ log.summary }}</span>
                          </td>
                          <td class="text-muted">{{ log.user_name }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add Product Allocation Modal -->
    <div v-if="showAddModal" class="modal-backdrop-custom" @click.self="closeAddModal">
      <div class="modal-dialog-custom">
        <div class="card shadow-lg">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <h6 class="mb-0">新增商品分配</h6>
            <button class="btn-close" @click="closeAddModal"></button>
          </div>
          <div class="card-body">
            <!-- Step 1: Search Product -->
            <div class="mb-3">
              <label class="form-label text-sm">搜尋商品 <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                  v-model="productSearch"
                  class="form-control"
                  placeholder="輸入商品名稱或編號"
                  @input="searchProducts"
                />
                <span class="input-group-text"><i class="fas fa-search"></i></span>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0" class="border rounded mt-1" style="max-height: 200px; overflow-y: auto">
                <div
                  v-for="p in searchResults"
                  :key="p.id"
                  class="p-2 cursor-pointer text-sm"
                  :class="{ 'bg-light fw-bold': addForm.product_id === p.id }"
                  style="cursor: pointer"
                  @click="selectProduct(p)"
                >
                  <div>{{ p.name }}</div>
                  <div class="text-xs text-muted font-monospace">{{ p.product_code }} · 庫存: {{ p.stock }}</div>
                </div>
              </div>

              <!-- Selected Product -->
              <div v-if="selectedProduct" class="mt-2 p-2 bg-light rounded text-sm">
                <strong>已選：</strong>{{ selectedProduct.name }}
                <span class="text-muted ms-1 font-monospace">{{ selectedProduct.product_code }}</span>
                <span class="ms-2 text-primary">總庫存: {{ selectedProduct.stock }}</span>
              </div>
            </div>

            <!-- Step 2: Choose Variant (if any) -->
            <div v-if="selectedProduct && productVariants.length > 0" class="mb-3">
              <label class="form-label text-sm">選擇規格 <span class="text-danger">*</span></label>
              <select v-model="addForm.product_variant_id" class="form-select">
                <option :value="null">— 選擇規格 —</option>
                <option v-for="v in productVariants" :key="v.id" :value="v.id">
                  {{ v.variant_code }}
                  <template v-if="v.display_name"> · {{ v.display_name }}</template>
                  (庫存: {{ v.stock }})
                </option>
              </select>
            </div>

            <!-- Step 3: Quantity -->
            <div class="mb-3" v-if="selectedProduct">
              <label class="form-label text-sm">寄賣數量 <span class="text-danger">*</span></label>
              <input
                v-model.number="addForm.allocated_stock"
                type="number"
                min="0"
                class="form-control"
                placeholder="0"
              />
              <div class="text-xs text-muted mt-1" v-if="addRemainingStock !== null">
                可分配上限：<strong class="text-primary">{{ addRemainingStock }}</strong>
              </div>
            </div>

            <div class="mb-3" v-if="selectedProduct">
              <label class="form-label text-sm">備註</label>
              <input v-model="addForm.notes" class="form-control" placeholder="選填" />
            </div>

            <div v-if="addError" class="alert alert-danger py-2 text-sm">{{ addError }}</div>
          </div>
          <div class="card-footer d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary btn-sm" @click="closeAddModal">取消</button>
            <button
              class="btn btn-primary btn-sm"
              :disabled="!selectedProduct || addForm.allocated_stock === null || adding"
              @click="addAllocation"
            >
              <span v-if="adding" class="spinner-border spinner-border-sm me-1"></span>
              確認新增
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";

export default {
  name: "WarehouseDetail",
  data() {
    return {
      warehouseId: null,
      warehouse: {},
      items: [],
      loading: false,

      // Inline edit
      editingItem: null,
      editForm: { allocated_stock: 0, sold_qty: 0, notes: "" },
      saving: false,

      // Log panel
      expandedLogId: null,
      itemLogs: [],
      logLoading: false,

      // Add modal
      showAddModal: false,
      productSearch: "",
      searchResults: [],
      selectedProduct: null,
      productVariants: [],
      addForm: {
        product_id: null,
        product_variant_id: null,
        allocated_stock: 0,
        notes: "",
      },
      addRemainingStock: null,
      addError: null,
      adding: false,
      searchTimer: null,
    };
  },
  computed: {
    totalAllocated() {
      return this.items.reduce((sum, i) => sum + (i.allocated_stock || 0), 0);
    },
    totalSold() {
      return this.items.reduce((sum, i) => sum + (i.sold_qty || 0), 0);
    },
    totalRemaining() {
      return this.items.reduce((sum, i) => sum + (i.remaining_in_store ?? i.allocated_stock ?? 0), 0);
    },
  },
  async created() {
    this.warehouseId = this.$route.params.id;
    await Promise.all([this.fetchWarehouse(), this.fetchInventory()]);
  },
  methods: {
    async fetchWarehouse() {
      try {
        const res = await request.get(`/api/admin/warehouses/${this.warehouseId}`);
        this.warehouse = res.data.data;
      } catch {
        this.$toast?.error("載入倉庫資訊失敗");
      }
    },
    async fetchInventory() {
      this.loading = true;
      try {
        const res = await request.get(`/api/admin/warehouses/${this.warehouseId}/inventory`);
        this.items = res.data.data;
      } catch {
        this.$toast?.error("載入庫存分配失敗");
      } finally {
        this.loading = false;
      }
    },

    // ─── Inline Edit ───────────────────────────────────────────────────────
    startEdit(item) {
      this.editingItem = item.id;
      this.editForm = {
        allocated_stock: item.allocated_stock,
        sold_qty: item.sold_qty ?? 0,
        notes: item.notes || "",
      };
    },

    async toggleLog(item) {
      if (this.expandedLogId === item.id) {
        this.expandedLogId = null;
        this.itemLogs = [];
        return;
      }
      this.expandedLogId = item.id;
      this.itemLogs = [];
      this.logLoading = true;
      try {
        const res = await request.get(`/api/admin/warehouses/${this.warehouseId}/logs`, {
          params: { inventory_id: item.id },
        });
        this.itemLogs = res.data.data || [];
      } catch {
        this.itemLogs = [];
      } finally {
        this.logLoading = false;
      }
    },

    fieldLabel(field) {
      const map = { allocated_stock: '寄賣數量', sold_qty: '已賣出', notes: '備註' };
      return map[field] || field;
    },    cancelEdit() {
      this.editingItem = null;
    },
    async saveEdit(item) {
      this.saving = true;
      try {
        await request.put(
          `/api/admin/warehouses/${this.warehouseId}/inventory/${item.id}`,
          this.editForm
        );
        this.$toast?.success("已更新");
        this.editingItem = null;
        await this.fetchInventory();
        // Refresh log panel if it was open for this item
        if (this.expandedLogId === item.id) {
          const res = await request.get(`/api/admin/warehouses/${this.warehouseId}/logs`, {
            params: { inventory_id: item.id },
          });
          this.itemLogs = res.data.data || [];
        }
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || "更新失敗");
      } finally {
        this.saving = false;
      }
    },
    async removeItem(item) {
      if (!confirm(`確定移除「${item.product_name}」的分配？`)) return;
      try {
        await request.delete(
          `/api/admin/warehouses/${this.warehouseId}/inventory/${item.id}`
        );
        this.$toast?.success("已移除");
        await this.fetchInventory();
      } catch {
        this.$toast?.error("移除失敗");
      }
    },

    // ─── Add Modal ─────────────────────────────────────────────────────────
    openAddModal() {
      this.showAddModal = true;
      this.productSearch = "";
      this.searchResults = [];
      this.selectedProduct = null;
      this.productVariants = [];
      this.addForm = { product_id: null, product_variant_id: null, allocated_stock: 0, notes: "" };
      this.addError = null;
      this.addRemainingStock = null;
    },
    closeAddModal() {
      this.showAddModal = false;
    },
    searchProducts() {
      clearTimeout(this.searchTimer);
      if (!this.productSearch.trim()) {
        this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(async () => {
        try {
          const res = await request.get("/api/admin/products", {
            params: { search: this.productSearch, per_page: 10, status: "active" },
          });
          this.searchResults = res.data.data || [];
        } catch {
          this.searchResults = [];
        }
      }, 300);
    },
    async selectProduct(product) {
      this.selectedProduct = product;
      this.addForm.product_id = product.id;
      this.addForm.product_variant_id = null;
      this.searchResults = [];
      this.productSearch = "";
      this.productVariants = [];

      try {
        const res = await request.get(`/api/admin/products/${product.id}/variants`);
        this.productVariants = res.data.data || [];
      } catch {
        this.productVariants = [];
      }

      this.updateRemainingStock();
    },
    updateRemainingStock() {
      if (!this.selectedProduct) {
        this.addRemainingStock = null;
        return;
      }
      const stockObj = this.addForm.product_variant_id
        ? this.productVariants.find((v) => v.id === this.addForm.product_variant_id)
        : this.selectedProduct;
      this.addRemainingStock = stockObj?.stock ?? null;
    },
    async addAllocation() {
      this.addError = null;
      if (!this.addForm.product_id) {
        this.addError = "請選擇商品";
        return;
      }
      if (this.productVariants.length > 0 && !this.addForm.product_variant_id) {
        this.addError = "此商品有規格，請選擇一個規格";
        return;
      }
      this.adding = true;
      try {
        await request.post(`/api/admin/warehouses/${this.warehouseId}/inventory`, this.addForm);
        this.$toast?.success("已新增分配");
        this.closeAddModal();
        await this.fetchInventory();
      } catch (e) {
        this.addError = e.response?.data?.message || "新增失敗";
      } finally {
        this.adding = false;
      }
    },
  },
  watch: {
    "addForm.product_variant_id"() {
      this.updateRemainingStock();
    },
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-dialog-custom {
  width: 100%;
  max-width: 580px;
}
</style>
